import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
	type ChangeEvent,
	type DragEvent,
	type KeyboardEvent,
	useCallback,
	useRef,
	useState
} from 'react';
import { useTranslation } from 'react-i18next';

import { useBreakpoint } from '@/utils/useBreakpoint';

import {
	ATTACHMENT_ACCEPT_ATTR,
	ATTACHMENT_MAX_FILES,
	type DcrAttachment
} from '../attachment.types';
import type { AddFilesResult } from '../useAttachmentUpload';

import { attachmentDropzoneStyles as sx } from './AttachmentDropzone.styles';

type Props = {
	attachments: DcrAttachment[];
	addFiles: (files: File[]) => AddFilesResult;
};

export const AttachmentDropzone = ({ attachments, addFiles }: Props) => {
	const { t } = useTranslation();
	const inputRef = useRef<HTMLInputElement>(null);
	const [isDraggingOver, setIsDraggingOver] = useState(false);
	const [errors, setErrors] = useState<string[]>([]);
	const isXs = useBreakpoint('sm', 'down');

	const slotsLeft = ATTACHMENT_MAX_FILES - attachments.length;
	const canAddMore = slotsLeft > 0;

	const handleFiles = useCallback(
		(files: File[]) => {
			if (files.length === 0) return;
			const result = addFiles(files);
			const newErrors = Array.from(new Set(result.rejected.map(r => r.error)));
			setErrors(newErrors);
		},
		[addFiles]
	);

	const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
		if (!e.target.files) return;
		handleFiles(Array.from(e.target.files));
		e.target.value = '';
	};

	const handleBrowseClick = () => {
		inputRef.current?.click();
	};

	const handleZoneKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			handleBrowseClick();
		}
	};

	const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
		if (canAddMore) setIsDraggingOver(true);
	};

	const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDraggingOver(false);
	};

	const handleDrop = (e: DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDraggingOver(false);
		if (!canAddMore) return;
		const files = Array.from(e.dataTransfer.files);
		handleFiles(files);
	};

	return (
		<Box sx={{ width: '100%' }}>
			<Box
				sx={sx.root(isDraggingOver, canAddMore)}
				onDragOver={handleDragOver}
				onDragEnter={handleDragOver}
				onDragLeave={handleDragLeave}
				onDrop={handleDrop}
				onClick={canAddMore ? handleBrowseClick : undefined}
				role={canAddMore ? 'button' : undefined}
				tabIndex={canAddMore ? 0 : undefined}
				onKeyDown={canAddMore ? handleZoneKeyDown : undefined}
			>
				{canAddMore && (
					<Box sx={sx.browseSection}>
						<input
							ref={inputRef}
							type='file'
							accept={ATTACHMENT_ACCEPT_ATTR}
							multiple
							style={sx.hiddenInput}
							onChange={handleFileInputChange}
						/>
						<FileUploadOutlinedIcon
							sx={sx.uploadIcon}
							data-slot='upload-icon'
						/>
						<Box sx={sx.textStack}>
							<Typography sx={sx.browseSubtext}>
								{t('Drop files or click to upload')}
							</Typography>
							{!isXs && (
								<Typography
									sx={sx.helperText(isDraggingOver)}
									data-slot='helper-text'
								>
									{t('Images only · max {{max}} files · up to 10 MB per file', {
										max: ATTACHMENT_MAX_FILES
									})}
								</Typography>
							)}
						</Box>
					</Box>
				)}

				{!canAddMore && (
					<Typography sx={sx.maxReachedText}>
						{t('Maximum {{max}} files reached', { max: ATTACHMENT_MAX_FILES })}
					</Typography>
				)}
			</Box>

			{errors.length > 0 && (
				<Box sx={sx.errorList}>
					{errors.map(err => (
						<Alert
							key={err}
							severity='error'
							onClose={() => setErrors(errors.filter(e => e !== err))}
						>
							{err}
						</Alert>
					))}
				</Box>
			)}
		</Box>
	);
};
